import { useState } from "react";
import { showSuccess, showError } from "@/lib/toastHelpers";
import { useQuery } from "@tanstack/react-query";
import { useApiMutation } from "@/hooks/useApiMutation";
import { useLocation } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

import { CreditCard, Plus } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { PlanForm } from "@/components/subscription-plans/PlanForm";
import { PlanTable } from "@/components/subscription-plans/PlanTable";

type SubscriptionPlan = {
  _id: string;
  name: string;
  description: string | null;
  priceMonthly: string;
  priceYearly: string | null;
  annualDiscountPercent: number;
  currency: string;
  features: string[];
  isActive: boolean;
  sortOrder: number;
  trialDurationDays?: number;
  maxRecipes?: number;
  maxIngredients?: number;
  maxMaterials?: number;
  unlimitedRecipes?: boolean;
  unlimitedIngredients?: boolean;
  unlimitedMaterials?: boolean;
  permissions?: {
    dashboard?: boolean;
    orders?: boolean;
    planner?: boolean;
    profitGoal?: boolean;
    analytics?: boolean;
    library?: boolean;
    libraryMyRecipes?: boolean;
    libraryBentoRecipes?: boolean;
    libraryStarterPack?: boolean;
    pantry?: boolean;
    pantryIngredients?: boolean;
    pantryMaterials?: boolean;
    pantrySuppliers?: boolean;
    team?: boolean;
    inventory?: boolean;
  };
  createdAt: string;
  updatedAt: string;
};

const defaultPermissions = {
  dashboard: true,
  orders: true,
  planner: true,
  profitGoal: true,
  menus: true,
  analytics: true,
  library: true,
  libraryMyRecipes: true,
  libraryBentoRecipes: true,
  libraryStarterPack: true,
  pantry: true,
  pantryIngredients: true,
  pantryMaterials: true,
  pantrySuppliers: true,
  team: true,
  inventory: true,
  sales: true,
  salesOrders: true,
  salesCustomers: true,
  salesReport: true,
};

export default function SubscriptionPlans() {
  
  const [, setLocation] = useLocation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [formData, setFormData] = useState<any>({
    name: "",
    description: "",
    priceMonthly: "",
    priceDaily: "",
    annualDiscountPercent: 0,
    currency: "PHP",
    features: "",
    isActive: true,
    sortOrder: 0,
    trialDurationDays: 0,
    maxRecipes: 0,
    maxIngredients: 0,
    maxMaterials: 0,
    unlimitedRecipes: false,
    unlimitedIngredients: false,
    unlimitedMaterials: false,
    permissions: defaultPermissions,
    paypalPlanId: "",
    paypalSubscriptionUrl: "",
    pricesByCurrency: {} as Record<string, { priceMonthly?: string; priceYearly?: string; priceDaily?: string }>,
  });

  const { data: plans = [] } = useQuery<SubscriptionPlan[]>({
    queryKey: ["/api/admin/subscription-plans"],
  });

  const calculateYearlyPrice = (monthly: string, discount: number): string => {
    const monthlyNum = parseFloat(monthly) || 0;
    if (monthlyNum === 0) return "";
    const yearlyBeforeDiscount = monthlyNum * 12;
    const yearlyAfterDiscount = yearlyBeforeDiscount * (1 - discount / 100);
    return yearlyAfterDiscount.toFixed(2);
  };

  const createMutation = useApiMutation({
    endpoint: "/api/admin/subscription-plans",
    method: "POST",
    invalidate: [["/api/admin/subscription-plans"]],
    successMessage: "Plan created successfully",
    errorMessage: "Failed to create plan",
    onSuccess: () => {
      resetForm();
    },
  });

  const updateMutation = useApiMutation({
    endpoint: (vars: { id: string }) => `/api/admin/subscription-plans/${vars.id}`,
    method: "PATCH",
    invalidate: [["/api/admin/subscription-plans"]],
    successMessage: "Plan updated successfully",
    errorMessage: "Failed to update plan",
    onSuccess: () => {
      setIsDialogOpen(false);
      resetForm();
    },
  });

  const deleteMutation = useApiMutation({
    endpoint: (vars: { id: string }) => `/api/admin/subscription-plans/${vars.id}`,
    method: "DELETE",
    invalidate: [["/api/admin/subscription-plans"]],
    successMessage: "Plan deleted successfully",
    errorMessage: "Failed to delete plan",
    onSuccess: () => {
      setDeleteConfirm(null);
    },
    onError: () => {
      setDeleteConfirm(null);
    },
  });

  const toggleActiveMutation = useApiMutation({
    endpoint: (vars: { id: string; isActive?: boolean }) => `/api/admin/subscription-plans/${vars.id}`,
    method: "PATCH",
    invalidate: [["/api/admin/subscription-plans"]],
    successMessage: "Plan status updated",
    errorMessage: "Failed to update plan status",
  });

  const toggleComingSoonMutation = useApiMutation({
    endpoint: (vars: { id: string; isComingSoon?: boolean }) => `/api/admin/subscription-plans/${vars.id}`,
    method: "PATCH",
    invalidate: [["/api/admin/subscription-plans"]],
    successMessage: "Plan updated",
    errorMessage: "Failed to update plan",
  });

  const resetForm = () => {
    setFormData({ 
      name: "", 
      description: "", 
      priceMonthly: "",
      priceDaily: "",
      annualDiscountPercent: 0, 
      currency: "PHP", 
      features: "", 
      isActive: true, 
      sortOrder: 0, 
      trialDurationDays: 0,
      maxRecipes: 0,
      maxIngredients: 0,
      maxMaterials: 0,
      unlimitedRecipes: false,
      unlimitedIngredients: false,
      unlimitedMaterials: false,
      permissions: defaultPermissions,
      paypalPlanId: "",
      paypalSubscriptionUrl: "",
      pricesByCurrency: {},
    });
    setEditingPlan(null);
    setIsDialogOpen(false);
  };

  const handleEdit = (plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    const newFormData = {
      name: plan.name,
      description: plan.description || "",
      priceMonthly: plan.priceMonthly,
      priceYearly: plan.priceYearly || "",
      priceDaily: (plan as any).priceDaily || "",
      annualDiscountPercent: plan.annualDiscountPercent,
      currency: plan.currency,
      features: plan.features.join('\n'),
      isActive: plan.isActive,
      sortOrder: plan.sortOrder,
      trialDurationDays: plan.trialDurationDays || 0,
      maxRecipes: plan.maxRecipes || 0,
      maxIngredients: plan.maxIngredients || 0,
      maxMaterials: plan.maxMaterials || 0,
      unlimitedRecipes: plan.unlimitedRecipes || false,
      unlimitedIngredients: plan.unlimitedIngredients || false,
      unlimitedMaterials: plan.unlimitedMaterials || false,
      permissions: { ...defaultPermissions, ...plan.permissions },
      paypalPlanId: (plan as any).paypalPlanId || "",
      paypalSubscriptionUrl: (plan as any).paypalSubscriptionUrl || "",
      pricesByCurrency: (plan as any).pricesByCurrency || {},
    };
    setFormData(newFormData);
    setIsDialogOpen(true);
  };

  const handleSubmit = () => {
    // Use manual yearly price if provided, otherwise calculate it
    const yearlyPrice = formData.priceYearly && formData.priceYearly.trim() !== '' 
      ? formData.priceYearly 
      : calculateYearlyPrice(formData.priceMonthly, formData.annualDiscountPercent);
    
    const payload = {
      ...formData,
      priceYearly: yearlyPrice,
      features: formData.features.split('\n').filter((f: string) => f.trim()),
    };
    
    if (editingPlan) {
      updateMutation.mutate({ id: editingPlan._id, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <div className="space-y-6">
      {/* Desktop: Full header */}
      <div className="hidden md:flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold tracking-tight">Subscription Plans</h1>
          <p className="text-muted-foreground">Manage pricing plans and features</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => resetForm()}>
              <Plus className="w-4 h-4 mr-2" />
              New Plan
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPlan ? "Edit" : "Create"} Subscription Plan</DialogTitle>
            </DialogHeader>
            <PlanForm
              formData={formData}
              onChange={(data) => setFormData({ ...formData, ...data })}
              onSubmit={handleSubmit}
              onCancel={resetForm}
              isLoading={createMutation.isPending || updateMutation.isPending}
              isEditing={!!editingPlan}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Mobile: Compact header */}
      <div className="md:hidden space-y-3">
        <h1 className="text-xl font-bold">Plans</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => resetForm()} className="w-full">
              <Plus className="w-4 h-4 mr-2" />
              New Plan
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPlan ? "Edit" : "Create"} Subscription Plan</DialogTitle>
            </DialogHeader>
            <PlanForm
              formData={formData}
              onChange={(data) => setFormData({ ...formData, ...data })}
              onSubmit={handleSubmit}
              onCancel={resetForm}
              isLoading={createMutation.isPending || updateMutation.isPending}
              isEditing={!!editingPlan}
            />
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            All Plans
          </CardTitle>
          <CardDescription>{plans.length} subscription plans</CardDescription>
        </CardHeader>
        <CardContent>
          <PlanTable
            plans={plans}
            onEdit={handleEdit as any}
            onDelete={(id) => setDeleteConfirm(id)}
            onToggleActive={(id, isActive) => toggleActiveMutation.mutate({ id, isActive })}
            onToggleComingSoon={(id, isComingSoon) => toggleComingSoonMutation.mutate({ id, isComingSoon })}
          />
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Plan</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this subscription plan? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteConfirm && deleteMutation.mutate({ id: deleteConfirm })}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
